import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  Check,
  Clock,
  AlertCircle,
  Eye,
  Trash2,
  Plus,
  X,
  Calendar,
  Building2,
  Upload,
  Edit,
  ChevronDown,
  CreditCard,
} from 'lucide-react';
import { financialPowerInvoicesAPI, vendorInvoicesAPI } from '../services/api';
import { User } from '../types';
import { formatCurrency, getCurrencyFontClass } from '../utils/currency';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import { confirmToast } from '../components/confirmToast';
import AttachmentViewer from '../components/AttachmentViewer';
import { decryptData } from '../utils/encryption';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';

interface FinancialInvoice {
  id: number;
  reference_number?: string;
  invoice_type?: string;
  amount?: number | string | null;
  status: string;
  created_at: string;
  created_by?: number;
  attachment_url?: string;
  attachment_filename?: string;
  attachment_size?: number;
  attachment_type?: string;
  finance_status?: 'pending' | 'approved' | 'rejected';
  status_display?: string;
  finance_account_no?: string;
  finance_account_name?: string;
  approver_details?: Array<{
    id: number;
    approver_id: number;
    approver_order: number;
    status: string;
    first_name: string;
    last_name: string;
    email: string;
    notes?: string | null;
    approved_at?: string | null;
  }>;
  approvers?: number[] | null;
  additional_attachments?: Array<{
    filename?: string;
    url?: string;
    size?: number;
    type?: string;
  }>;
  financial_power?: {
    vendor_id?: number;
    department_id?: number;
    invoice_type?: string;
    reference_number?: string;
    reference_name?: string;
    vendor_name?: string;
    vendor_email?: string;
    vendor_phone?: string;
    vendor_address?: string;
    department_name?: string;
    currency?: string;
  };
}

const FinancialPowerInvoice: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userPermissionsLocalStorage = localStorage.getItem('permissions') || '';
  const userPermissions = decryptData(userPermissionsLocalStorage);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter] = useState<'all' | string>('all');
  const [financialInvoices, setFinancialInvoices] = useState<FinancialInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<FinancialInvoice | null>(null);
  const [loadingInvoiceDetails, setLoadingInvoiceDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<FinancialInvoice | null>(null);
  const [updating, setUpdating] = useState(false);
  const [amount, setAmount] = useState<string>('');
  const [additionalAttachments, setAdditionalAttachments] = useState<File[]>([]);
  const [approvers, setApprovers] = useState<string[]>(['']);
  const [approverSearchTerms, setApproverSearchTerms] = useState<string[]>(['']);
  const [approverDropdownOpenIndex, setApproverDropdownOpenIndex] = useState<number | null>(null);
  const [availableApprovers, setAvailableApprovers] = useState<User[]>([]);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [approverNotes, setApproverNotes] = useState<{ [key: number]: string }>({});
  const [financeForm, setFinanceForm] = useState<{ account_number: string; account_name: string }>({
    account_number: '',
    account_name: '',
  });
  const [financeErrors, setFinanceErrors] = useState<{ account_number?: string; account_name?: string }>({});
  const [isSubmittingFinance, setIsSubmittingFinance] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paid_amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
    reference_number: '',
    broucher: null as File | null
  });
  const [paymentErrors, setPaymentErrors] = useState<{ reference_number?: string }>({});
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [auditTrailData, setAuditTrailData] = useState<any>(null);

  // Attachment viewer modal states
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState<string>('');
  const [currentAttachmentName, setCurrentAttachmentName] = useState<string>('');
  const additionalAttachmentsRef = useRef<HTMLInputElement>(null);
  const approverDropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
      total_pages: 0,
    });

  const stats = useMemo(() => {
    return {
      total: pagination.total,
      approved: financialInvoices.filter((invoice) => invoice.status === 'approved').length,
      pending: financialInvoices.filter((invoice) => invoice.status === 'pending' || invoice.status?.startsWith('pending_')).length,
      underReview: financialInvoices.filter((invoice) => invoice.status?.startsWith('pending_approver_')).length
    };
  }, [financialInvoices, pagination.total]);

  useEffect(() => {
    // fetchFinancialInvoices();
    fetchApprovers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        approverDropdownOpenIndex !== null &&
        approverDropdownRefs.current[approverDropdownOpenIndex] &&
        !approverDropdownRefs.current[approverDropdownOpenIndex]?.contains(event.target as Node)
      ) {
        setApproverDropdownOpenIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [approverDropdownOpenIndex]);

  const fetchApprovers = async () => {
    try {
      const response = await vendorInvoicesAPI.invoiceApproverDropdown();
      if (response?.success) {
        setAvailableApprovers(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch approvers', error);
    }
  };

  const fetchFinancialInvoices = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await financialPowerInvoicesAPI.getAll(params);
      if (response?.success) {
        setFinancialInvoices(response.data?.financial_power_invoices || []);
        if (response.data?.pagination) {
          setPagination({
            page: response.data.pagination.page || 1,
            limit: response.data.pagination.limit || 20,
            total: response.data.pagination.total || 0,
            totalPages: response.data.pagination.total_pages || response.data.pagination.totalPages || 0,
            total_pages: response.data.pagination.total_pages || response.data.pagination.totalPages || 0,
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch financial power invoices', error);
      errorModal('Failed to load financial power invoices');
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchFinancialInvoices();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [pagination.page, statusFilter]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pagination.page === 1) {
        fetchFinancialInvoices();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  const handleDeleteInvoice = async (id: number) => {
    confirmToast('Are you sure you want to delete this financial power invoice? This action cannot be undone.', async () => {
      setDeleting(true);
      try {
        const response = await financialPowerInvoicesAPI.delete(id);
        if (response?.success) {
          toast.success('Financial power invoice deleted successfully');
          fetchFinancialInvoices();
        } else {
          errorModal(response?.error || 'Failed to delete invoice');
        }
      } catch (error: any) {
        console.error('Failed to delete invoice', error);
        errorModal(error?.response?.data?.error || 'Failed to delete invoice');
      } finally {
        setDeleting(false);
      }
    });
  };

  // Handle viewing attachment in modal
  const handleViewAttachment = (url: string, filename: string) => {
    if (!url) {
      errorModal('No attachment URL available');
      return;
    }
    setCurrentAttachmentUrl(url);
    setCurrentAttachmentName(filename);
    setShowAttachmentViewer(true);
  };

  const closeAttachmentViewer = () => {
    setShowAttachmentViewer(false);
    setCurrentAttachmentUrl('');
    setCurrentAttachmentName('');
  };

  const handleViewInvoice = async (id: number) => {
    const invoice = financialInvoices.find(inv => inv.id === id);
    if (invoice) {
      setSelectedInvoice(invoice);
      setShowInvoiceModal(true);
      setLoadingInvoiceDetails(true);
      
      try {
        const response = await financialPowerInvoicesAPI.getById(id);
        if (response?.success) {
          setSelectedInvoice(response.data);
          // Initialize finance form with existing values if any
          if (response.data.finance_account_no || response.data.finance_account_name) {
            setFinanceForm({
              account_number: response.data.finance_account_no || '',
              account_name: response.data.finance_account_name || '',
            });
          } else {
            setFinanceForm({ account_number: '', account_name: '' });
          }
        }
        
        // Fetch audit trail data
        try {
          const auditResponse = await financialPowerInvoicesAPI.getAuditTrail(id);
          if (auditResponse?.success) {
            setAuditTrailData(auditResponse.data);
          }
        } catch (auditError) {
          console.error('Error fetching audit trail:', auditError);
        }
      } catch (error) {
        console.error('Failed to fetch invoice details', error);
        errorModal('Failed to load invoice details');
      } finally {
        setLoadingInvoiceDetails(false);
      }
    }
  };

  const handleEditInvoice = (invoice: FinancialInvoice) => {
    setEditingInvoice(invoice);
    setAmount(invoice.amount?.toString() || '');
    setAdditionalAttachments([]);
    
    // Load existing approvers if any
    if (invoice.approvers && Array.isArray(invoice.approvers) && invoice.approvers.length > 0) {
      setApprovers(invoice.approvers.map((a: number) => a.toString()));
      setApproverSearchTerms(invoice.approvers.map(() => ''));
    } else {
      setApprovers(['']);
      setApproverSearchTerms(['']);
    }
    
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingInvoice(null);
    setAmount('');
    setAdditionalAttachments([]);
    setApprovers(['']);
    setApproverSearchTerms(['']);
    if (additionalAttachmentsRef.current) {
      additionalAttachmentsRef.current.value = '';
    }
  };

  const isVariablePayment = () => {
    return editingInvoice?.invoice_type?.toLowerCase() === 'variable' || 
           editingInvoice?.invoice_type?.toLowerCase() === 'variable_payment' ||
           editingInvoice?.financial_power?.invoice_type?.toLowerCase() === 'variable';
  };

  const handleAdditionalAttachmentsChange = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    const existing = [...additionalAttachments];
    Array.from(files).forEach((file) => {
      if (existing.length < 20) {
        existing.push(file);
      }
    });
    setAdditionalAttachments(existing);

    if (additionalAttachmentsRef.current) {
      additionalAttachmentsRef.current.value = '';
    }
  };

  const handleRemoveAdditionalAttachment = (index: number) => {
    setAdditionalAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddApprover = () => {
    if (approvers.length < 10) {
      setApprovers(prev => [...prev, '']);
      setApproverSearchTerms(prev => [...prev, '']);
    }
  };

  const handleRemoveApprover = (index: number) => {
    if (approvers.length > 1) {
      setApprovers(prev => prev.filter((_, i) => i !== index));
      setApproverSearchTerms(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleApproverSelect = (index: number, approverId: string) => {
    const newApprovers = [...approvers];
    newApprovers[index] = approverId;
    setApprovers(newApprovers);
    setApproverDropdownOpenIndex(null);
    setApproverSearchTerms(prev => {
      const newTerms = [...prev];
      newTerms[index] = '';
      return newTerms;
    });
  };

  const handleUpdateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingInvoice) return;

    // Validate amount
    if (!amount.trim()) {
      errorModal('Amount is required');
      return;
    }

    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      errorModal('Please enter a valid amount');
      return;
    }

    // Validate approvers for variable payment
    if (isVariablePayment()) {
      const validApprovers = approvers.filter(a => a !== '');
      const hasExistingApprovers = editingInvoice.approvers && editingInvoice.approvers.length > 0;
      
      if (!hasExistingApprovers && validApprovers.length === 0) {
        errorModal('At least one approver is required for variable payment invoices');
        return;
      }
    }

    setUpdating(true);
    try {
      const formData = new FormData();

      // Add amount
      formData.append('amount', amount);

      // Add additional attachments if any
      additionalAttachments.forEach((file) => {
        formData.append('additional_attachments', file);
      });

      // For variable payment, update approvers if assigned
      if (isVariablePayment() && approvers.some(a => a !== '')) {
        const validApprovers = approvers.filter(a => a !== '').map(a => parseInt(a));
        if (validApprovers.length > 0) {
          formData.append('approvers', JSON.stringify(validApprovers));
        }
      }

      const response = await financialPowerInvoicesAPI.update(editingInvoice.id, formData);
      
      if (response?.success) {
        toast.success('Invoice updated successfully');
        handleCloseEditModal();
        // Refresh invoice details if modal is open
        if (showInvoiceModal && selectedInvoice?.id === editingInvoice.id) {
          const invoiceResponse = await financialPowerInvoicesAPI.getById(editingInvoice.id);
          if (invoiceResponse?.success) {
            setSelectedInvoice(invoiceResponse.data);
          }
        }
        // Refresh the list
        fetchFinancialInvoices();
      } else {
        errorModal(response?.error || 'Failed to update invoice');
      }
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      errorModal(error?.response?.data?.error || 'Failed to update invoice');
    } finally {
      setUpdating(false);
    }
  };

  const handleApproverDecision = async (
    approverId: number,
    decision: 'approved' | 'rejected'
  ) => {
    if (!selectedInvoice) return;
    
    try {
      setIsSubmittingApproval(true);
      const notes = approverNotes[approverId] || '';

      // Call the approve API
      const response = await financialPowerInvoicesAPI.approve(
        selectedInvoice.id,
        decision,
        notes
      );
      
      if (response?.success) {
        toast.success(`Invoice ${decision} successfully`);
        // Refresh invoice data
        const updatedResponse = await financialPowerInvoicesAPI.getById(selectedInvoice.id);
        if (updatedResponse?.success) {
          setSelectedInvoice(updatedResponse.data);
          // Clear notes
          setApproverNotes({});
        }
        // Refresh audit trail
        try {
          const auditResponse = await financialPowerInvoicesAPI.getAuditTrail(selectedInvoice.id);
          if (auditResponse?.success) {
            setAuditTrailData(auditResponse.data);
          }
        } catch (auditError) {
          console.error('Error fetching audit trail:', auditError);
        }
        // Refresh the list
        fetchFinancialInvoices();
      } else {
        errorModal(response?.error || `Failed to ${decision} invoice`);
      }
    } catch (error: any) {
      console.error('Approver decision failed:', error);
      errorModal(error?.response?.data?.error || `Failed to ${decision} invoice`);
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const handleFinanceFieldChange = (
    field: 'account_number' | 'account_name',
    value: string
  ) => {
    if (field === 'account_number') {
      const sanitized = value.replace(/\D/g, '').slice(0, 20);
      setFinanceForm((prev) => ({ ...prev, account_number: sanitized }));
      setFinanceErrors((prev) => ({ ...prev, account_number: undefined }));
    } else {
      const sanitized = value.replace(/\s{2,}/g, ' ').replace(/^\s+/, '').slice(0, 100);
      setFinanceForm((prev) => ({ ...prev, account_name: sanitized }));
      setFinanceErrors((prev) => ({ ...prev, account_name: undefined }));
    }
  };

  const handleFinanceDecision = async (decision: 'approved' | 'rejected') => {
    if (!selectedInvoice) return;

    let accountNumber = financeForm.account_number.trim();
    let accountName = financeForm.account_name.trim();

    if (decision === 'approved') {
      const errors: { account_number?: string; account_name?: string } = {};
      if (!accountNumber) {
        errors.account_number = 'Account number is required';
      } else if (!/^\d+$/.test(accountNumber)) {
        errors.account_number = 'Account number must include digits only';
      } else if (accountNumber.length > 20) {
        errors.account_number = 'Account number must be 20 digits or fewer';
      }

      if (!accountName) {
        errors.account_name = 'Account name is required';
      } else if (accountName.length < 2) {
        errors.account_name = 'Account name must be at least 2 characters';
      }

      if (Object.keys(errors).length > 0) {
        setFinanceErrors(errors);
        return;
      }
    } else {
      setFinanceErrors({});
    }

    try {
      setIsSubmittingFinance(true);
      const formData = new FormData();
      formData.append('finance_status', decision);
      if (decision === 'approved') {
        formData.append('finance_account_no', accountNumber);
        formData.append('finance_account_name', accountName);
        formData.append('finance_status', "pending at payment processor");
      }

      const response = await financialPowerInvoicesAPI.update(selectedInvoice.id, formData);

      if (response?.success) {
        toast.success(`Invoice ${decision} by finance team successfully`);
        // Refresh invoice data
        const updatedResponse = await financialPowerInvoicesAPI.getById(selectedInvoice.id);
        if (updatedResponse?.success) {
          setSelectedInvoice(updatedResponse.data);
          if (decision === 'approved') {
            setFinanceForm({
              account_number: accountNumber,
              account_name: accountName,
            });
          }
        }
        // Refresh audit trail
        try {
          const auditResponse = await financialPowerInvoicesAPI.getAuditTrail(selectedInvoice.id);
          if (auditResponse?.success) {
            setAuditTrailData(auditResponse.data);
          }
        } catch (auditError) {
          console.error('Error fetching audit trail:', auditError);
        }
        // Refresh the list
        fetchFinancialInvoices();
      } else {
        errorModal(response?.error || `Failed to ${decision} invoice`);
      }
    } catch (error: any) {
      console.error('Finance decision failed:', error);
      errorModal(error?.response?.data?.error || `Failed to ${decision} invoice`);
    } finally {
      setIsSubmittingFinance(false);
    }
  };

  const handleCreateInvoice = () => {
    navigate('/financial-power-invoice/create');
  };

  const handleMarkPaid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    const errors: { reference_number?: string } = {};

    if (!paymentForm.reference_number.trim()) {
      errors.reference_number = 'Reference Number is required.';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      return;
    }

    setPaymentErrors({});
    setIsSubmittingPayment(true);
    try {
      if (paymentForm.broucher) {
        await financialPowerInvoicesAPI.markPaidWithAttachment(selectedInvoice.id, {
          paid_amount: parseFloat(paymentForm.paid_amount),
          payment_date: paymentForm.payment_date,
          notes: paymentForm.notes,
          reference_number: paymentForm.reference_number,
          payment_attachement: paymentForm.broucher,
        });
      } else {
        await financialPowerInvoicesAPI.markPaid(
          selectedInvoice.id,
          parseFloat(paymentForm.paid_amount),
          paymentForm.payment_date,
          paymentForm.notes,
          paymentForm.reference_number
        );
      }

      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setPaymentForm({
        paid_amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: '',
        reference_number: '',
        broucher: null
      });
      setPaymentErrors({});
      
      // Refresh invoice data
      const updatedResponse = await financialPowerInvoicesAPI.getById(selectedInvoice.id);
      if (updatedResponse?.success) {
        setSelectedInvoice(updatedResponse.data);
      }
      // Refresh audit trail
      try {
        const auditResponse = await financialPowerInvoicesAPI.getAuditTrail(selectedInvoice.id);
        if (auditResponse?.success) {
          setAuditTrailData(auditResponse.data);
        }
      } catch (auditError) {
        console.error('Error fetching audit trail:', auditError);
      }
      // Refresh the list
      fetchFinancialInvoices();
    } catch (error: any) {
      console.error('Error recording payment:', error);
      errorModal(error?.response?.data?.error || 'Error recording payment. Please try again.');
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentErrors({});
  };

  const formatInvoiceType = (type: string | undefined): string => {
    if (!type) return 'N/A';
    // Handle both "variable_payment" and "variable" formats
    const normalizedType = type.replace('_payment', '').replace('_', ' ');
    return normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1).toLowerCase() + ' Payment';
  };

  const getStatusBadge = (status: string | undefined, statusDisplay?: string) => {
    // Use status_display if available, otherwise use status
    const displayText = statusDisplay || status || 'Pending';
    
    let config: { bg: string; text: string; label: string };
    
    // Determine badge color based on status (for logic) but use display text for label
    const statusForLogic = status || '';
    
    if (statusForLogic === 'approved' || displayText.toLowerCase().includes('approved')) {
      config = { bg: 'bg-green-100 text-green-800', text: 'Approved', label: displayText };
    } else if (statusForLogic === 'rejected' || displayText.toLowerCase().includes('rejected')) {
      config = { bg: 'bg-red-100 text-red-800', text: 'Rejected', label: displayText };
    } else if (statusForLogic?.startsWith('pending_approver_') || displayText.toLowerCase().includes('approver')) {
      config = { bg: 'bg-blue-100 text-blue-800', text: 'Under Review', label: displayText };
    } else if (displayText.toLowerCase().includes('finance')) {
      config = { bg: 'bg-blue-100 text-blue-800', text: 'Finance', label: displayText };
    } else if (displayText.toLowerCase().includes('payment processor')) {
      config = { bg: 'bg-purple-100 text-purple-800', text: 'Payment', label: displayText };
    } else if (displayText.toLowerCase().includes('paid')) {
      config = { bg: 'bg-green-100 text-green-800', text: 'Paid', label: displayText };
    } else {
      config = { bg: 'bg-yellow-100 text-yellow-800', text: 'Pending', label: displayText };
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg}`}>
        {config.label.charAt(0).toUpperCase() + config.label.slice(1).toLowerCase()}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Power Invoice</h1>
          <p className="mt-1 text-sm text-gray-600">
            Track invoices that require financial power approval across departments.
          </p>
        </div>
        {(userPermissions === "invoice_initiator" || userPermissions === "admin" || userPermissions === "finance_team" || userPermissions === "payment_approver") && (
          <button
            className="btn-primary self-start md:self-auto flex items-center space-x-2"
            onClick={handleCreateInvoice}
          >
            {(userPermissions === "admin" || userPermissions === "finance_team" || userPermissions === "payment_approver") ? <Eye className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            <span>{(userPermissions === "admin"  || userPermissions === "payment_approver" || userPermissions === "finance_team") ? "View All Invoices" : "Create New Invoice"}</span>
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-primary-100 rounded-lg flex-shrink-0">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Total Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Approved</p>
                <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Under Review</p>
                <p className="text-2xl font-bold text-gray-900">{stats.underReview}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="input-field pl-10"
                  placeholder="Search invoices, vendors, or references..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            {/* <div className="w-full sm:w-64">
              <select
                className="input-field w-full"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div> */}
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="card">
        <div className="card-header flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-900">Financial Power Invoices</h3>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading invoices...</p>
            </div>
          ) : financialInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
              <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">ID</th>
                      <th className="table-header-cell">Vendor</th>
                      <th className="table-header-cell">Department</th>
                      <th className="table-header-cell">Invoice Type</th>
                      <th className="table-header-cell">Amount</th>
                      <th className="table-header-cell">Created Date</th>
                      <th className="table-header-cell">Status</th>
                      <th className="table-header-cell">Reference Number</th>
                      <th className="table-header-cell">Reference Name</th>
                      <th className="table-header-cell text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {financialInvoices.map((invoice) => (
                      <tr key={invoice.id} className="table-row">
                        <td className="table-cell">
                          <div>
                            <p className="text-sm font-medium text-gray-900">FPI-{invoice.id}</p>
                          </div>
                        </td>
                        <td className="table-cell">
                          <p className="text-sm text-gray-900">{invoice.financial_power?.vendor_name || 'N/A'}</p>
                        </td>
                        <td className="table-cell">
                          <p className="text-sm text-gray-900">{invoice.financial_power?.department_name || 'N/A'}</p>
                        </td>
                        <td className="table-cell">
                          <p className="text-sm text-gray-900">
                            {formatInvoiceType(invoice.invoice_type || invoice.financial_power?.invoice_type)}
                          </p>
                        </td>
                        <td className="table-cell">
                          {invoice.amount ? (
                            <p className={`text-sm font-medium text-gray-900 ${getCurrencyFontClass(invoice.financial_power?.currency || 'GBP')}`}>
                              {formatCurrency(invoice.amount, invoice.financial_power?.currency || 'GBP')}
                            </p>
                          ) : (
                            <p className="text-sm text-gray-500">-</p>
                          )}
                        </td>
                        <td className="table-cell">
                          <p className="text-sm text-gray-900">
                            {new Date(invoice.created_at).toLocaleDateString('en-GB')}
                          </p>
                        </td>
                        <td className="table-cell">
                          {getStatusBadge(invoice.status, invoice.status_display)}
                        </td>
                        <td className="table-cell">
                          <p className="text-sm text-gray-900">{invoice.reference_number || invoice.financial_power?.reference_number || 'N/A'}</p>
                        </td>
                        <td className="table-cell">
                          <p className="text-sm text-gray-900">{invoice.financial_power?.reference_name || 'N/A'}</p>
                        </td>
                        <td className="table-cell text-right space-x-2">
                          <div className="flex items-center justify-end space-x-2">
                            <button 
                              onClick={() => handleViewInvoice(invoice.id)}
                              className="text-primary-600 hover:text-primary-900" 
                              title="View Invoice"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {userPermissions === 'payment_approver' && invoice.status != 'paid' && (
                              <button 
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setPaymentForm({
                                    paid_amount: invoice.amount?.toString() || '0',
                                    payment_date: new Date().toISOString().split('T')[0],
                                    notes: '',
                                    reference_number: '',
                                    broucher: null
                                  });
                                  setShowPaymentModal(true);
                                }}
                                className="text-green-600 hover:text-green-900" 
                                title="Record Payment"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            )}
                            {invoice.status === 'pending' && (
                            <button 
                              onClick={() => handleDeleteInvoice(invoice.id)}
                              disabled={deleting}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50" 
                              title="Delete Invoice"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination && (
                <Pagination
                  pagination={{
                    page: pagination.page,
                    limit: pagination.limit,
                    total: pagination.total,
                    total_pages: pagination.total_pages || pagination.totalPages,
                  }}
                  onPageChange={(p) => setPagination(prev => ({ 
                    ...prev, 
                    page: p,
                    totalPages: prev.total_pages || prev.totalPages,
                  }))}
                  onLimitChange={(l) => {
                    const newLimit = Math.min(100, Math.max(1, l));
                    setPagination(prev => ({ 
                      ...prev, 
                      limit: newLimit, 
                      page: 1,
                      totalPages: prev.total_pages || prev.totalPages,
                    }));
                  }}
                  loading={loading}
                  className="px-4 pb-4"
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Invoice Detail Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-5xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Financial Power Invoice Details</h3>
                    <p className="text-primary-100 text-sm">FPI-{selectedInvoice.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowInvoiceModal(false);
                    setSelectedInvoice(null);
                    setApproverNotes({});
                    setFinanceForm({ account_number: '', account_name: '' });
                    setFinanceErrors({});
                    setAuditTrailData(null);
                  }}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            {loadingInvoiceDetails ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <p className="mt-2 text-sm text-gray-500">Loading invoice details...</p>
              </div>
            ) : (
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* Left Column - Basic Info */}
                  <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                    {/* Invoice Overview Card */}
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">Invoice Overview</h4>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(selectedInvoice.status, selectedInvoice.status_display)}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                              <FileText className="h-4 w-4 text-primary-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Invoice ID</p>
                              <p className="text-sm font-medium text-gray-900">FPI-{selectedInvoice.id}</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                              <Calendar className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Created Date</p>
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(selectedInvoice.created_at).toLocaleDateString('en-GB')}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                              <Building2 className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Invoice Type</p>
                              <p className="text-sm font-medium text-gray-900">
                                {formatInvoiceType(selectedInvoice.invoice_type || selectedInvoice.financial_power?.invoice_type)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                              <CreditCard className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Amount</p>
                              {selectedInvoice.amount ? (
                                <p className={`text-sm font-medium text-gray-900 ${getCurrencyFontClass(selectedInvoice.financial_power?.currency || 'GBP')}`}>
                                  {formatCurrency(selectedInvoice.amount, selectedInvoice.financial_power?.currency || 'GBP')}
                                </p>
                              ) : (
                                <p className="text-sm font-medium text-gray-500">-</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Financial Summary Card */}
                    <div className="bg-gradient-to-r from-green-50 to-primary-50 rounded-lg p-4 sm:p-6 border border-green-100">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reference Number</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedInvoice.reference_number || selectedInvoice.financial_power?.reference_number || 'N/A'}
                          </p>
                        </div>
                        {selectedInvoice.amount && (
                          <div className="text-center sm:text-right">
                            <div className={`text-2xl font-semibold text-gray-700 ${getCurrencyFontClass(selectedInvoice.financial_power?.currency || 'GBP')}`}>
                              {formatCurrency(selectedInvoice.amount, selectedInvoice.financial_power?.currency || 'GBP')}
                            </div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Invoice Amount</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Approvers Section */}
                    {selectedInvoice.approver_details && selectedInvoice.approver_details.length > 0 && (
                      <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h4 className="text-lg font-semibold text-gray-900">Invoice Approval</h4>
                        </div>
                        <div className="p-6">
                          <div className="space-y-4">
                            {selectedInvoice.approver_details
                              .sort((a: any, b: any) => (a.approver_order || 0) - (b.approver_order || 0))
                              .map((approver: any, index: number) => {
                                const isCurrentUserApprover = String(user?.id || '') === String(approver.approver_id || '');
                                const canApprove = isCurrentUserApprover && 
                                  approver.status !== 'approved' && 
                                  approver.status !== 'rejected';
                                const isApproved = approver.status === 'approved';
                                const isRejected = approver.status === 'rejected';

                                return (
                                  <div
                                    key={approver.id || index}
                                    className={`p-4 rounded-lg border transition-all duration-200 ${
                                      isApproved
                                        ? 'bg-green-50 border-green-200 hover:border-green-300'
                                        : isRejected
                                        ? 'bg-red-50 border-red-200 hover:border-red-300'
                                        : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:border-orange-300'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-3">
                                      <div>
                                        <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">
                                          LEVEL {approver.approver_order || index + 1} APPROVER
                                        </p>
                                      </div>
                                      <span
                                        className={`text-xs font-semibold capitalize ${
                                          isApproved
                                            ? 'text-green-700'
                                            : isRejected
                                            ? 'text-red-700'
                                            : 'text-gray-600'
                                        }`}
                                      >
                                        {approver.status || 'Pending'}
                                      </span>
                                    </div>
                                    <div
                                      className={`bg-white rounded-lg p-3 border ${
                                        isApproved
                                          ? 'border-green-100'
                                          : isRejected
                                          ? 'border-red-100'
                                          : 'border-orange-100'
                                      }`}
                                    >
                                      <div className="flex items-center">
                                        <div
                                          className={`${
                                            isApproved
                                              ? 'bg-green-100'
                                              : isRejected
                                              ? 'bg-red-100'
                                              : 'bg-gradient-to-br from-orange-100 to-orange-200'
                                          } w-8 h-8 rounded-full flex items-center justify-center mr-3`}
                                        >
                                          <span
                                            className={`text-xs font-semibold ${
                                              isApproved
                                                ? 'text-green-700'
                                                : isRejected
                                                ? 'text-red-700'
                                                : 'text-orange-700'
                                            }`}
                                          >
                                            {approver.first_name?.charAt(0) || ''}
                                            {approver.last_name?.charAt(0) || ''}
                                          </span>
                                        </div>
                                        <div>
                                          <p className="text-sm font-semibold text-gray-900">
                                            {approver.first_name} {approver.last_name}
                                          </p>
                                          <p className="text-xs text-gray-600">{approver.email}</p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Note and action area */}
                                    {canApprove ? (
                                      <div className="mt-3 space-y-2">
                                        <label className="block text-xs font-medium text-gray-700">Note</label>
                                        <textarea
                                          rows={3}
                                          className="input-field w-full"
                                          value={approverNotes[approver.approver_id] || ''}
                                          onChange={(e) => {
                                            setApproverNotes(prev => ({
                                              ...prev,
                                              [approver.approver_id]: e.target.value
                                            }));
                                          }}
                                          placeholder="Enter your note..."
                                        />

                                        <div className="flex items-center space-x-2">
                                          <button
                                            type="button"
                                            disabled={isSubmittingApproval}
                                            onClick={() => handleApproverDecision(approver.approver_id, 'rejected')}
                                            className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                          >
                                            Reject
                                          </button>
                                          <button
                                            type="button"
                                            disabled={isSubmittingApproval}
                                            onClick={() => handleApproverDecision(approver.approver_id, 'approved')}
                                            className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                          >
                                            Approve
                                          </button>
                                        </div>
                                      </div>
                                    ) : approver.notes ? (
                                      <div className="mt-3 p-3 bg-gray-50 border rounded">
                                        <p className="text-xs font-semibold text-gray-700 mb-1">Note</p>
                                        <p className="text-sm text-gray-800">{approver.notes}</p>
                                        {approver.approved_at && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            {new Date(approver.approved_at).toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Finance Team Section */}
                    {(() => {
                      // Check if all approvers have approved (for variable payment) or if it's fixed payment
                      const isVariablePayment = selectedInvoice.invoice_type?.toLowerCase() === 'variable' || 
                                               selectedInvoice.invoice_type?.toLowerCase() === 'variable_payment' ||
                                               selectedInvoice.financial_power?.invoice_type?.toLowerCase() === 'variable';
                      
                      const allApproversApproved = !isVariablePayment || 
                        (selectedInvoice.approver_details && 
                         selectedInvoice.approver_details.length > 0 &&
                         selectedInvoice.approver_details.every((a: any) => a.status === 'approved'));
                      
                      const invoiceApproved = selectedInvoice.status === 'approved' || allApproversApproved;
                      
                      // Check if finance has already processed (account details exist)
                      const financeProcessed = !!(selectedInvoice.finance_account_no && selectedInvoice.finance_account_name);
                      
                      // Show finance section if:
                      // 1. Invoice is approved (all approvers approved or fixed payment)
                      // 2. OR finance has already processed it (account details exist)
                      const canShowFinanceSection = invoiceApproved || financeProcessed;
                      
                      // Determine finance status for display
                      const getFinanceStatusDisplay = () => {
                        if (selectedInvoice.finance_status === 'approved') return 'Approved';
                        if (selectedInvoice.finance_status === 'rejected') return 'Rejected';
                        if (financeProcessed) {
                          // If account details exist but status is null, check status_display
                          if (selectedInvoice.status_display?.toLowerCase().includes('payment processor')) {
                            return 'Approved';
                          }
                          return 'Approved';
                        }
                        return 'Pending';
                      };
                      
                      // Determine if finance can still edit (show input fields)
                      const canFinanceEdit = userPermissions === 'finance_team' && 
                                            !financeProcessed && 
                                            selectedInvoice.finance_status !== 'approved' && 
                                            selectedInvoice.finance_status !== 'rejected';
                      
                      return canShowFinanceSection ? (
                        <div className={`p-4 rounded-lg border transition-all duration-200 ${
                          financeProcessed || selectedInvoice.finance_status === 'approved'
                            ? 'bg-green-50 border-green-200 hover:border-green-300'
                            : selectedInvoice.finance_status === 'rejected'
                            ? 'bg-red-50 border-red-200 hover:border-red-300'
                            : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-300'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">FINANCE TEAM</p>
                            </div>
                            <span className={`text-xs font-semibold capitalize ${
                              financeProcessed || selectedInvoice.finance_status === 'approved'
                                ? 'text-green-700'
                                : selectedInvoice.finance_status === 'rejected'
                                ? 'text-red-700'
                                : 'text-gray-600'
                            }`}>
                              {getFinanceStatusDisplay()}
                            </span>
                          </div>
                          <div className={`bg-white rounded-lg p-3 border ${
                            financeProcessed || selectedInvoice.finance_status === 'approved'
                              ? 'border-green-100'
                              : selectedInvoice.finance_status === 'rejected'
                              ? 'border-red-100'
                              : 'border-blue-100'
                          }`}>
                            {canFinanceEdit ? (
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Account Number <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className="input-field"
                                    value={financeForm.account_number}
                                    onChange={(e) => handleFinanceFieldChange('account_number', e.target.value)}
                                    placeholder="Enter account number"
                                    disabled={isSubmittingFinance}
                                  />
                                  {financeErrors.account_number && (
                                    <p className="text-xs text-red-600 mt-1">{financeErrors.account_number}</p>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Account Name <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="input-field"
                                    value={financeForm.account_name}
                                    onChange={(e) => handleFinanceFieldChange('account_name', e.target.value)}
                                    placeholder="Enter account name"
                                    disabled={isSubmittingFinance}
                                  />
                                  {financeErrors.account_name && (
                                    <p className="text-xs text-red-600 mt-1">{financeErrors.account_name}</p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2 pt-1">
                                  <button
                                    type="button"
                                    disabled={isSubmittingFinance}
                                    onClick={() => handleFinanceDecision('rejected')}
                                    className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    Reject
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isSubmittingFinance || !financeForm.account_number || !financeForm.account_name.trim()}
                                    onClick={() => handleFinanceDecision('approved')}
                                    className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                  >
                                    Approve
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {financeProcessed ? (
                                  <>
                                    <div>
                                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Number</p>
                                      <p className="text-sm font-semibold text-gray-900">
                                        {selectedInvoice.finance_account_no}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Name</p>
                                      <p className="text-sm font-semibold text-gray-900">
                                        {selectedInvoice.finance_account_name}
                                      </p>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div>
                                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Number</p>
                                      <p className="text-sm font-semibold text-gray-500">
                                        Awaiting finance team
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Name</p>
                                      <p className="text-sm font-semibold text-gray-500">
                                        Awaiting finance team
                                      </p>
                                    </div>
                                  </>
                                )}
                                {selectedInvoice.finance_status === 'rejected' && (
                                  <p className="text-xs text-red-600">Finance team rejected this invoice.</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {/* Additional Attachments */}
                    {selectedInvoice.additional_attachments && Array.isArray(selectedInvoice.additional_attachments) && selectedInvoice.additional_attachments.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <div className="flex items-center mb-4">
                          <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center mr-3">
                            <Upload className="h-4 w-4 text-gray-600" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Additional Attachments</h4>
                            <p className="text-xs text-gray-500">{selectedInvoice.additional_attachments.length} file(s)</p>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {selectedInvoice.additional_attachments.map((attachment: any, index: number) => (
                            <div
                              key={index}
                              className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all duration-200"
                            >
                              <div className="flex items-center flex-1 min-w-0">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center mr-3">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">{attachment.filename || 'Attachment'}</p>
                                  <p className="text-xs text-gray-500">
                                    {attachment.size ? `${(attachment.size / 1024).toFixed(1)} KB` : attachment.type || 'Attachment'}
                                  </p>
                                </div>
                              </div>
                              {attachment.url && (
                                <button
                                  onClick={() => handleViewAttachment(attachment.url, attachment.filename || 'Attachment')}
                                  className="ml-3 px-3 py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 hover:text-primary-800 transition-colors duration-200 flex items-center"
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  <span>View</span>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Quick Info */}
                  <div className="space-y-4 sm:space-y-6">
                    {/* Quick Actions Card */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h4>
                      <div className="space-y-3">
                        {(selectedInvoice.status === 'pending') && (
                          <button
                            onClick={() => {
                              setShowInvoiceModal(false);
                              handleEditInvoice(selectedInvoice);
                            }}
                            className="flex items-center justify-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors w-full"
                          >
                            <Edit className="h-4 w-4" />
                            <span>Edit Invoice</span>
                          </button>
                        )}
                        {userPermissions === 'payment_approver' && 
                         selectedInvoice.finance_account_no && 
                         selectedInvoice.finance_account_name &&
                         selectedInvoice.status_display?.toLowerCase().includes('payment processor') && (
                          <button
                            onClick={() => {
                              setPaymentForm({
                                paid_amount: selectedInvoice.amount?.toString() || '0',
                                payment_date: new Date().toISOString().split('T')[0],
                                notes: '',
                                reference_number: '',
                                broucher: null
                              });
                              setShowPaymentModal(true);
                            }}
                            className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors w-full"
                          >
                            <Check className="h-4 w-4" />
                            <span>Record Payment</span>
                          </button>
                        )}
                        {selectedInvoice.attachment_url && (
                          <button
                            onClick={() => handleViewAttachment(selectedInvoice.attachment_url || '', selectedInvoice.attachment_filename || 'Invoice')}
                            className="flex items-center justify-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors w-full"
                          >
                            <Eye className="h-4 w-4" />
                            <span>View Invoice</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Proposal Info Card */}
                    {selectedInvoice.financial_power && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Proposal Information</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reference Number</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedInvoice.financial_power.reference_number || 'N/A'}
                            </p>
                          </div>
                          {selectedInvoice.financial_power.reference_name && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reference Name</p>
                              <p className="text-sm font-medium text-gray-900">
                                {selectedInvoice.financial_power.reference_name}
                              </p>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Vendor</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedInvoice.financial_power.vendor_name || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Department</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedInvoice.financial_power.department_name || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Invoice Status Trail */}
                    {auditTrailData && auditTrailData.audit_trail && auditTrailData.audit_trail.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status</h4>

                        <div className="relative">
                          <div className="space-y-3">
                            {auditTrailData.audit_trail
                              .slice()
                              .reverse()
                              .map((trail: any, index: number, reversedArray: any[]) => {
                                // Check if this trail item contains "Rejected" keyword
                                const statusText = trail.action === 'UPDATE' && trail.description?.includes('Status changed')
                                  ? trail.details?.new_status
                                  : trail.description;
                                const isRejected = statusText && statusText.toLowerCase().includes('rejected');
                                
                                return (
                                  <div key={trail.id || index} className="relative flex items-start">
                                    {/* Timeline line segment - connects to next item (below current item) */}
                                    {index < reversedArray.length - 1 && (
                                      <div className={`absolute left-3 top-6 w-0.5 z-0 ${isRejected ? 'bg-red-600' : 'bg-green-300'}`} style={{ height: 'calc(100% + 0.75rem)' }}></div>
                                    )}
                                    {/* Timeline dot */}
                                    <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white shadow-sm ${isRejected ? 'bg-red-600' : 'bg-green-600'}`}>
                                    </div>

                                    {/* Content */}
                                    <div className={`ml-3 flex-1 rounded-md p-3 border ${isRejected ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <span className={`text-sm font-semibold capitalize ${isRejected ? 'text-red-900' : 'text-gray-900'}`}>
                                          {statusText || trail.description || 'Action performed'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {new Date(trail.performed_at).toLocaleString()}
                                        </span>
                                      </div>

                                      <div className="text-xs text-gray-600 mb-1">
                                        By: {trail.performed_by?.name || 'Unknown'} {trail.performed_by?.email ? `(${trail.performed_by.email})` : ''}
                                      </div>

                                      {/* Show approver details when approvers are assigned */}
                                      {trail.action === 'UPDATE' && trail.description?.includes('Approvers') && trail.details?.approver_details && (
                                        <div className="space-y-0.5 mt-2">
                                          {trail.details.approver_details.map((approver: any, idx: number) => (
                                            <div key={idx} className="text-xs text-gray-600">
                                              <span className="font-medium">Level {approver.approver_order} Approver:</span> {approver.approver_name || approver.approver_email}
                                            </div>
                                          ))}
                                        </div>
                                      )}

                                      {/* Show payment details */}
                                      {(trail.details?.paid_amount || trail.details?.reference_number || trail.details?.payment_date || trail.details?.notes) && (
                                        <div className="mt-2 grid grid-cols-1 gap-2">
                                          {trail.details.paid_amount && (
                                            <div className="flex flex-col bg-green-50 rounded border-l-4 border-green-400 px-3 py-2">
                                              <span className="text-xs text-green-700 font-semibold">Paid Amount</span>
                                              <span className="text-sm text-green-900">{trail.details.paid_amount}</span>
                                            </div>
                                          )}
                                          {trail.details.reference_number && (
                                            <div className="flex flex-col bg-yellow-50 rounded border-l-4 border-yellow-400 px-3 py-2">
                                              <span className="text-xs text-yellow-700 font-semibold">Reference Number</span>
                                              <span className="text-sm text-yellow-900">{trail.details.reference_number}</span>
                                            </div>
                                          )}
                                          {trail.details.payment_date && (
                                            <div className="flex flex-col bg-primary-50 rounded border-l-4 border-primary-400 px-3 py-2">
                                              <span className="text-xs text-primary-700 font-semibold">Payment Date</span>
                                              <span className="text-sm text-primary-900">
                                                {trail.details.payment_date ? new Date(trail.details.payment_date).toLocaleDateString() : ''}
                                              </span>
                                            </div>
                                          )}
                                          {trail.details.notes && (
                                            <div className="flex flex-col bg-gray-50 rounded border-l-4 border-gray-400 px-3 py-2">
                                              <span className="text-xs text-gray-700 font-semibold">Notes</span>
                                              <span className="text-sm text-gray-900">{trail.details.notes}</span>
                                            </div>
                                          )}
                                          {trail.details.payment_attachement?.attachment_url && (
                                            <button
                                              onClick={() => handleViewAttachment(trail.details.payment_attachement.attachment_url, trail.details.payment_attachement.filename || 'Payment Attachment')}
                                              className="flex flex-col bg-gray-50 rounded border-l-4 border-gray-400 px-3 py-2 text-left hover:bg-gray-100 w-full text-left"
                                              title="View payment attachment"
                                            >
                                              <span className="text-xs text-gray-700 font-semibold">Payment Attachment</span>
                                              <span className="text-sm text-primary-600 underline truncate">{trail.details.payment_attachement.filename}</span>
                                            </button>
                                          )}
                                        </div>
                                      )}

                                      {/* Show finance status details */}
                                      {trail.details?.finance_status && (
                                        <div className="mt-2 grid grid-cols-1 gap-2">
                                          <div className="flex flex-col bg-blue-50 rounded border-l-4 border-blue-400 px-3 py-2">
                                            <span className="text-xs text-blue-700 font-semibold">Finance Status</span>
                                            <span className="text-sm text-blue-900 capitalize">{trail.details.new_finance_status || trail.details.finance_status}</span>
                                          </div>
                                          {trail.details.finance_account_no && (
                                            <div className="flex flex-col bg-blue-50 rounded border-l-4 border-blue-400 px-3 py-2">
                                              <span className="text-xs text-blue-700 font-semibold">Account Number</span>
                                              <span className="text-sm text-blue-900">{trail.details.finance_account_no}</span>
                                            </div>
                                          )}
                                          {trail.details.finance_account_name && (
                                            <div className="flex flex-col bg-blue-50 rounded border-l-4 border-blue-400 px-3 py-2">
                                              <span className="text-xs text-blue-700 font-semibold">Account Name</span>
                                              <span className="text-sm text-blue-900">{trail.details.finance_account_name}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Show amount update details */}
                                      {trail.details?.old_amount && trail.details?.new_amount && (
                                        <div className="mt-2 grid grid-cols-1 gap-2">
                                          <div className="flex flex-col bg-purple-50 rounded border-l-4 border-purple-400 px-3 py-2">
                                            <span className="text-xs text-purple-700 font-semibold">Amount Updated</span>
                                            <span className="text-sm text-purple-900">
                                              {trail.details.old_amount} → {trail.details.new_amount}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {showEditModal && editingInvoice && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Edit Financial Power Invoice</h3>
                <button
                  onClick={handleCloseEditModal}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={handleUpdateInvoice} className="space-y-6">
                {/* Amount Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {editingInvoice.financial_power?.currency || 'GBP'}
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`input-field flex-1 ${getCurrencyFontClass(editingInvoice.financial_power?.currency || 'GBP')}`}
                      placeholder="Enter invoice amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the invoice amount in {editingInvoice.financial_power?.currency || 'GBP'}
                  </p>
                </div>

                {/* Additional Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Attachments
                  </label>
                  <input
                    ref={additionalAttachmentsRef}
                    type="file"
                    multiple
                    className="input-field"
                    onChange={(e) => handleAdditionalAttachmentsChange(e.target.files)}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.csv"
                    disabled={updating}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional supporting documents — up to 20 files
                  </p>
                  {additionalAttachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {additionalAttachments.map((file, idx) => (
                        <div
                          key={`${file.name}-${idx}`}
                          className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-700"
                        >
                          <span className="truncate pr-3">{file.name}</span>
                          <button
                            type="button"
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => handleRemoveAdditionalAttachment(idx)}
                            disabled={updating}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Approvers Section - Only for Variable Payment */}
                {isVariablePayment() && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Approvers <span className="text-red-500">*</span>
                      </label>
                      {approvers.length < 10 && (
                        <button
                          type="button"
                          onClick={handleAddApprover}
                          className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                          disabled={updating}
                        >
                          <Plus className="h-4 w-4" />
                          <span>Add Approver</span>
                        </button>
                      )}
                    </div>
                    <div className="space-y-3">
                      {approvers.map((approverId, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <div ref={(el) => (approverDropdownRefs.current[index] = el)} className="relative flex-1">
                            <button
                              type="button"
                              className="input-field w-full flex items-center justify-between text-left"
                              onClick={() => {
                                setApproverDropdownOpenIndex(approverDropdownOpenIndex === index ? null : index);
                              }}
                              disabled={updating}
                            >
                              <span className={approverId ? 'text-gray-900' : 'text-gray-500'}>
                                {approverId
                                  ? availableApprovers.find((a) => a.id.toString() === approverId)?.first_name + ' ' + 
                                    availableApprovers.find((a) => a.id.toString() === approverId)?.last_name + 
                                    ` (${availableApprovers.find((a) => a.id.toString() === approverId)?.email})`
                                  : `Select Approver ${index + 1}`}
                              </span>
                              <ChevronDown
                                className={`h-4 w-4 text-gray-400 transition-transform ${
                                  approverDropdownOpenIndex === index ? 'rotate-180' : ''
                                }`}
                              />
                            </button>
                            {approverDropdownOpenIndex === index && (
                              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
                                <div className="p-2 border-b border-gray-100">
                                  <div className="relative">
                                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                    <input
                                      type="text"
                                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
                                      placeholder="Search approvers..."
                                      value={approverSearchTerms[index] || ''}
                                      onChange={(e) => {
                                        const newTerms = [...approverSearchTerms];
                                        newTerms[index] = e.target.value;
                                        setApproverSearchTerms(newTerms);
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {availableApprovers
                                    .filter((user) => {
                                      const searchTerm = (approverSearchTerms[index] || '').toLowerCase();
                                      const fullName = `${user.first_name} ${user.last_name} ${user.email || ''}`.toLowerCase();
                                      return fullName.includes(searchTerm);
                                    })
                                    .map((user) => (
                                      <button
                                        key={user.id}
                                        type="button"
                                        className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900"
                                        onClick={() => handleApproverSelect(index, user.id.toString())}
                                      >
                                        {user.first_name} {user.last_name} ({user.email})
                                      </button>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {approvers.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveApprover(index)}
                              className="text-red-600 hover:text-red-700"
                              disabled={updating}
                            >
                              <X className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      For variable payment, approvers must approve before invoice goes to finance team
                    </p>
                  </div>
                )}

                {/* Fixed Payment Info */}
                {!isVariablePayment() && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Fixed Payment:</strong> This invoice will go directly to finance team and payment processor. No approvers required.
                    </p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    className="btn-secondary w-full sm:w-auto"
                    disabled={updating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary w-full sm:w-auto"
                    disabled={updating || !amount.trim() || (isVariablePayment() && !editingInvoice.approvers?.length && approvers.every(a => a === ''))}
                  >
                    {updating ? 'Updating...' : 'Update Invoice'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Payment Processing Details</h3>
                <button
                  onClick={handleClosePaymentModal}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <form onSubmit={handleMarkPaid} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paid Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    disabled
                    className="input-field"
                    value={paymentForm.paid_amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paid_amount: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
                  <input
                    type="date"
                    required
                    className="input-field"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number *</label>
                  <input
                    className="input-field"
                    value={paymentForm.reference_number}
                    placeholder='Enter reference number'
                    onChange={(e) => {
                      const value = e.target.value;
                      setPaymentForm({ ...paymentForm, reference_number: value });
                      if (paymentErrors.reference_number) {
                        setPaymentErrors((prev) => {
                          const next = { ...prev };
                          delete next.reference_number;
                          return next;
                        });
                      }
                    }}
                  />
                  {paymentErrors.reference_number && (
                    <p className="mt-1 text-sm text-red-600">{paymentErrors.reference_number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    rows={3}
                    className="input-field"
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder="Add any notes about this payment..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Broucher (PDF)</label>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="input-field"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) {
                        setPaymentForm({ ...paymentForm, broucher: null });
                        return;
                      }
                      const isPdf = file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));
                      if (!isPdf) {
                        errorModal('Please upload a PDF file only.');
                        (e.target as HTMLInputElement).value = '';
                        setPaymentForm({ ...paymentForm, broucher: null });
                        return;
                      }
                      setPaymentForm({ ...paymentForm, broucher: file });
                    }}
                  />
                  <p className="mt-1 text-xs text-gray-500">Upload a PDF attachment for the payment.</p>
                </div>

                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClosePaymentModal}
                    className="btn-secondary w-full sm:w-auto"
                    disabled={isSubmittingPayment}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingPayment}
                    className="btn-primary w-full sm:w-auto"
                  >
                    {isSubmittingPayment ? 'Recording...' : 'Record Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      <AttachmentViewer
        isOpen={showAttachmentViewer}
        url={currentAttachmentUrl}
        filename={currentAttachmentName}
        onClose={closeAttachmentViewer}
      />
    </div>
  );
};

export default FinancialPowerInvoice;
